import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
const StatusChip = ({ status }) => {
  const statusConfig = {
    New: {
      color: '#2563EB',
      bgcolor: '#EAF2FF',
    },
    'In Review': {
      color: '#EA7A1A',
      bgcolor: '#FFF2E7',
    },
    'Pending Info': {
      color: '#5B4CE6',
      bgcolor: '#F0ECFF',
    },
    Approved: {
      color: '#16A34A',
      bgcolor: '#EAFBF0',
    },
    Rejected: {
      color: '#DC2626',
      bgcolor: '#FDECEC',
    },
  };

  const { color, bgcolor } = statusConfig[status] || {
    color: '#64748b',
    bgcolor: '#f1f5f9',
  };

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        color,
        bgcolor,
        fontWeight: 600,
        
        height: 20,
        borderRadius: '6px',
        px: 0.5,
        '& .MuiChip-label': {
          px: 1,
        },
      }}
    />
  );
};

const formatTimestamp = (value) =>
  value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';

const getBatchId = (batch = {}) =>
  batch?.batchId ?? batch?.batch_id ?? batch?.id ?? batch?.batchIdentifier ?? '';

const getSource = (batch) =>
  batch?.batchRequests?.[0]?.request?.source ?? batch?.source ?? '-';

const getNdcCount = (batch) =>
  Array.isArray(batch?.batchRequests) ? batch?.batchRequests.length : 0;

const getGroupLabel = (request) =>
  request?.xptPgnDesc || request?.npaProductSum || '-';

const getBrandLabel = (brands) => {
  if (!Array.isArray(brands) || brands.length === 0) return '-';
  const matched = brands.find((item) => item.isMatched);
  if (matched?.brandName) {
    return matched.brandName;
  }
  if (brands.length === 1) {
    return brands[0].brandName ?? '-';
  }
  return 'MULTIPLE';
};

const getRequestId = (item) =>
  item?.request?.requestId ||
  item?.request?.request_id ||
  item?.requestId ||
  item?.request_id ||
  item?.id ||
  '';

const extractBatches = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const renderStatusChip = (value) => {
  const label = value ?? '-';
  const normalized = String(value ?? '').trim().toLowerCase();

  const isSuccess = normalized === 'closed' || normalized === 'completed';
  const isPending = normalized === 'open' || normalized === 'pending' || normalized === 'in_progress' || normalized === 'in progress';
  const isError = normalized === 'failed' || normalized === 'error' || normalized === 'rejected';

  let color = '#64748b';
  let bgColor = '#f1f5f9';
  let Icon = HelpOutlineRoundedIcon;

  if (isSuccess) {
    color = '#059669';
    bgColor = '#ecfdf5';
    Icon = CheckCircleRoundedIcon;
  } else if (isPending) {
    color = '#2563eb';
    bgColor = '#eff6ff';
    Icon = ScheduleRoundedIcon;
  } else if (isError) {
    color = '#dc2626';
    bgColor = '#fef2f2';
    Icon = ErrorRoundedIcon;
  }

  return (
    <Chip
      label={label}
      size="small"
      icon={<Icon style={{ fontSize: '1rem', color: 'inherit' }} />}
      sx={{
        height: 24,
        borderRadius: '6px',
        fontWeight: 700,
        
        textTransform: 'uppercase',
        letterSpacing: '0.02em',
        backgroundColor: bgColor,
        color: color,
        border: '1px solid',
        borderColor: alpha(color, 0.2),
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

const DrugHistory = () => {
  const navigate = useNavigate();
  const { loading, error, data, execute } = useAPICall();
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const batches = useMemo(() => extractBatches(data), [data]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const columns = useMemo(() => [
    { header: 'Batch ID', id: 'batchId', accessorFn: (row) => row.batchId, size: 120 },
    { header: 'Source', id: 'source', accessorFn: (row) => getSource(row), size: 120 },
    { header: 'Status', id: 'status', accessorFn: (row) => row.batchStatus, size: 120, Cell: ({ cell }) => <StatusChip status={cell.getValue()} /> },
    { header: 'NDC Count', id: 'ndcCount', accessorFn: (row) => getNdcCount(row), size: 130 },
    { header: 'Created Date', id: 'createdDate', accessorFn: (row) => formatTimestamp(row.batchCreatedTime), size: 160 },
    { header: 'Last Updated', id: 'lastUpdated', accessorFn: (row) => formatTimestamp(row.batchLastUpdatedTime), size: 160 },
  ], []);
  const fetchHistory = useCallback(async () => {
    try {
      await execute(API_CONFIG.drugDcr.getClosed, null, {
        errorMessage: 'Unable to load drug history.',
        retries: 1,
      });
    } catch {
      // Already handled
    }
  }, [execute]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (batches.length === 0) {
      setSelectedBatchId('');
      return;
    }

    setSelectedBatchId((current) => {
      if (current && batches.some((batch) => getBatchId(batch) === current)) {
        return current;
      }
      return getBatchId(batches[0]);
    });
  }, [batches]);

  const selectedBatch = useMemo(
    () => batches.find((batch) => getBatchId(batch) === selectedBatchId) ?? null,
    [batches, selectedBatchId]
  );

  const batchRequests = useMemo(
    () =>
      Array.isArray(selectedBatch?.batchRequests) ? selectedBatch.batchRequests : [],
    [selectedBatch]
  );

  const openRequestDetails = (item) => {
    const requestId = getRequestId(item);
    if (!requestId) return;
    navigate(`/workflow/request-details/${requestId}`);
  };

  const softSearchSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      transition: 'all 0.2s',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#1E88E5', borderWidth: '1.5px' },
      '&.Mui-focused': { backgroundColor: '#fff' }
    },
    '& .MuiOutlinedInput-input': { py: 1,  }
  };

  return (
    <Box sx={{ pb: 1 }}>
      {/* <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 600,  color: '#0f172a' }}>
          Drug History
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          Audit trail and historical drug batch records.
        </Typography>
      </Box> */}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
             <GlobalMaterialTable
                state={{ isLoading: loading }}
                columns={columns}
            data={batches}
            emptyMessage="No closed batches available"
            enablePagination
              onRowClick={(_, row) => {
                 setSelectedBatchId(row?.original?.batchId)
              }}
              enableBottomToolbar
              manualPagination={false}
              page={pagination.pageIndex + 1}
              pageSize={pagination.pageSize}
              onPageChange={(p) => setPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
              onPageSizeChange={(ps) => setPagination(prev => ({ ...prev, pageSize: ps }))}
              selectedRowId={selectedBatchId}
              initialState={{ density: 'compact' }}
              enableStickyHeader
              tableTitle="Closed Batches"
              tableCount={batches.length}
              muiSearchTextFieldProps={{
                size: 'small',
                placeholder: 'Filter batches...',
                sx: softSearchSx,
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  ...(row?.original?.batchId === selectedBatchId
                    ? {
                        backgroundColor: '#EAF4FF !important',
                      '&:hover': {
                        backgroundColor: '#EAF4FF !important',
                      },
                      }
                    : {
                        '&:hover': { backgroundColor: '#f8fafc' }
                      }),
                },
              })}
           />
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              backgroundColor: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontWeight: 600,  color: '#1e293b' }}>Batch Details</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                    Inspecting Batch ID: <span style={{ color: '#1E88E5', fontWeight: 700 }}>{selectedBatchId || '-'}</span>
                  </Typography>
                </Box>
                <Button className="action-btn cancel-btn"
                  size="small"
                  variant="text"
                  onClick={() => setSelectedBatchId('')}
                  disabled={!selectedBatchId}
                  sx={{ textTransform: 'none', fontWeight: 700, color: '#ef4444' }}
                >
                  Clear selection
                </Button>
              </Box>

              {selectedBatch ? (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Created Time</Typography>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>{formatTimestamp(selectedBatch.batchCreatedTime)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Modified Time</Typography>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>{formatTimestamp(selectedBatch.batchLastUpdatedTime)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Status</Typography>
                      <Box sx={{ mt: 0.5 }}><StatusChip status={selectedBatch?.batchStatus} /> </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Source</Typography>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>{getSource(selectedBatch)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>NDC Count</Typography>
                      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>{getNdcCount(selectedBatch)}</Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 4 }}>
                    <GlobalMaterialTable
                      columns={[
                        { header: 'NDC', id: 'ndc', accessorFn: (row) => row.request?.ndc || '-' },
                        { header: 'Source', id: 'source', accessorFn: (row) => row.request?.source || '-' },
                        { header: 'Group', id: 'group', accessorFn: (row) => getGroupLabel(row.request) },
                        { header: 'Brand', id: 'brand', accessorFn: (row) => getBrandLabel(row.brand) },
                        { header: 'USC3', id: 'usc3', accessorFn: (row) => row.request?.usc3 || '-' },
                        { header: 'USC5', id: 'usc5', accessorFn: (row) => row.request?.usc5 || '-' },
                        { header: 'Updated', id: 'updated', accessorFn: (row) => formatTimestamp(row.lastUpdatedTime) },
                        { header: 'Status', id: 'status', accessorFn: (row) => row.status || '-', Cell: ({ cell }) => renderStatusChip(cell.getValue()) },
                        // {
                        //   header: 'Action',
                        //   id: 'action',
                        //   enableSorting: false,
                        //   size: 80,
                        //   Cell: ({ row }) => (
                        //     <Button className="action-btn close-btn"
                        //       size="small"
                        //       variant="text"
                        //       onClick={() => openRequestDetails(row.original)}
                        //       disabled={!getRequestId(row.original)}
                        //       sx={{ textTransform: 'none', fontWeight: 700, color: '#1E88E5' }}
                        //     >
                        //       View
                        //     </Button>
                        //   ),
                        // },
                      ]}
                      data={batchRequests}
                      enablePagination={true}
                      enableBottomToolbar={true}
                      tableTitle="Batch Requests"
                      tableCount={batchRequests.length}
                      muiSearchTextFieldProps={{
                        size: 'small',
                        placeholder: 'Search requests...',
                        sx: softSearchSx,
                      }}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #e2e8f0' }}>
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                    Select a batch from the list above to inspect its requests.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
    </Box>
  );
};

export default DrugHistory;
